/**
 * ChatGPT Service for enhanced conversational AI
 * Integrates with OpenAI's ChatGPT API for natural language processing
 */

import axios from 'axios';

export interface ChatGPTMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatGPTResponse {
  choices: Array<{
    message: {
      role: string;
      content: string;
    };
  }>;
}

class ChatGPTService {
  private apiKey: string;
  private baseURL: string = 'https://api.openai.com/v1/chat/completions';

  constructor() {
    // In production, this should come from environment variables
    this.apiKey = process.env.REACT_APP_OPENAI_API_KEY || '';
  }

  /**
   * Check if ChatGPT is available (has API key)
   */
  isAvailable(): boolean {
    return !!this.apiKey && this.apiKey.length > 0;
  }

  /**
   * Send a message to ChatGPT and get a response
   */
  async sendMessage(
    userMessage: string,
    context?: string,
    financialData?: string
  ): Promise<string> {
    if (!this.isAvailable()) {
      throw new Error('ChatGPT API key not configured');
    }

    try {
      const systemPrompt = this.buildSystemPrompt(context, financialData);
      const messages: ChatGPTMessage[] = [
        {
          role: 'system',
          content: systemPrompt
        },
        {
          role: 'user',
          content: userMessage
        }
      ];

      const response = await axios.post<ChatGPTResponse>(
        this.baseURL,
        {
          model: 'gpt-3.5-turbo',
          messages: messages,
          max_tokens: 500,
          temperature: 0.7,
          presence_penalty: 0.1,
          frequency_penalty: 0.1
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`
          },
          timeout: 10000 // 10 second timeout
        }
      );

      return response.data.choices[0]?.message?.content || 'Sorry, I could not generate a response.';
    } catch (error) {
      console.error('ChatGPT API error:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Invalid ChatGPT API key');
        } else if (error.response?.status === 429) {
          throw new Error('ChatGPT API rate limit exceeded');
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('ChatGPT API request timeout');
        }
      }
      
      throw new Error('Failed to connect to ChatGPT');
    }
  }

  /**
   * Build system prompt for ChatGPT based on context and financial data
   */
  private buildSystemPrompt(context?: string, financialData?: string): string {
    let prompt = `You are a helpful financial assistant AI integrated with real-time market data. You can provide:

1. **Real-time Financial Data**: Stock quotes, market indices, trending stocks, financial news, and currency exchange rates
2. **General Conversation**: Friendly, helpful responses about finance and markets
3. **Financial Analysis**: Basic insights and explanations about market data

**IMPORTANT GUIDELINES:**
- Always be helpful, accurate, and professional
- When discussing financial data, emphasize that it's real-time information
- If users ask for specific financial data (stocks, currencies, news), direct them to use the specific query features
- Keep responses concise but informative
- Use emojis sparingly and appropriately
- If you don't know something, say so rather than guessing

**Available Financial Features:**
- Stock quotes: "Get quote for AAPL" or "Price of TSLA"
- Market trends: "What stocks are trending?" or "Show trending stocks"
- Market overview: "How are the markets?" or "Get market indices"
- Financial news: "Get financial news" or "News about Apple"
- Currency rates: "USD to EUR rate" or "Exchange rate GBP/USD"`;

    if (context) {
      prompt += `\n\n**Current Context:** ${context}`;
    }

    if (financialData) {
      prompt += `\n\n**Available Financial Data:** ${financialData}`;
    }

    prompt += `\n\nRespond naturally and helpfully to user queries. If they're asking for financial data, guide them to use the appropriate commands.`;

    return prompt;
  }

  /**
   * Generate a response for financial queries with context
   */
  async generateFinancialResponse(
    userQuery: string,
    financialData: string
  ): Promise<string> {
    const context = `The user is asking about financial data. Here's the real-time data from the market: ${financialData}`;
    return this.sendMessage(userQuery, context, financialData);
  }

  /**
   * Generate a general conversational response
   */
  async generateGeneralResponse(userMessage: string): Promise<string> {
    return this.sendMessage(userMessage, 'General conversation mode');
  }
}

// Export singleton instance
export const chatGPTService = new ChatGPTService();
